import { matchBreakpoint } from '@chh/ui/js/matchBreakpoint';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// Ignore sub-pixel/jitter scrolls before toggling header visibility
const SCROLL_DELTA_THRESHOLD = 5;

// Mutually exclusive positioning states for the scroll-reveal behaviour
const HeaderState = {
  FLOW: 'flow', // in normal flow, scrolls away with the page
  PINNED: 'pinned', // fixed and visible (revealed on scroll up)
  UNPINNED: 'unpinned', // fixed and slid out of view (hidden on scroll down)
};

class HeaderElement extends HTMLElement {
  connectedCallback() {
    this._toggle = this.querySelector('[data-ref="toggle"]');
    this._nav = this.querySelector('[data-ref="mobilenav"]');
    this._mobileNav = this._nav.querySelector('chh-mobile-nav');
    this._focusTrap = this.querySelector('chh-focus-trap');
    this._header = this.querySelector('.header');

    this._controller = new AbortController();
    const { signal } = this._controller;

    this._toggle.addEventListener('click', () => this._handleToggle(), { signal });

    this._tabletQuery = matchBreakpoint('tablet');
    this._tabletQuery.addEventListener('change', () => this._handleBreakpointChange(), { signal });

    this._lastScrollY = window.scrollY;
    this._ticking = false;
    this._searchOpen = false;
    this._state = HeaderState.FLOW;
    this._desktopQuery = matchBreakpoint('desktop');
    this._desktopQuery.addEventListener('change', () => this._handleDesktopChange(), { signal });

    // Keep the header visible while the search panel is open, so scrolling
    // doesn't hide the input the user is typing into
    document.addEventListener('chh-search:open', () => this._handleSearchToggle(true), { signal });
    document.addEventListener('chh-search:close', () => this._handleSearchToggle(false), {
      signal,
    });

    window.addEventListener('scroll', () => this._onScroll(), { passive: true, signal });
    // Reveal the header when a descendant is focused (e.g. keyboard tabbing) while hidden
    this._header.addEventListener('focusin', () => this._reveal(), { signal });
  }

  disconnectedCallback() {
    this._controller?.abort();
  }

  _handleToggle() {
    const isOpen = this._toggle.getAttribute('aria-expanded') === 'true';
    this._setOpen(!isOpen);

    if (!isOpen) {
      this._nav.querySelector(FOCUSABLE_SELECTOR)?.focus();
    }
  }

  _handleBreakpointChange() {
    // The toggle is hidden at tablet and up, so close the nav when crossing into it
    if (this._tabletQuery.matches) {
      this._setOpen(false);
    }
  }

  _handleDesktopChange() {
    // Scroll-reveal only runs on desktop; return to normal flow elsewhere
    if (this._desktopQuery.matches) {
      this._lastScrollY = window.scrollY;
    } else {
      this._setState(HeaderState.FLOW);
    }
  }

  _handleSearchToggle(open) {
    this._searchOpen = open;
    if (open && this._desktopQuery.matches) {
      this._setState(HeaderState.PINNED);
    }
  }

  _onScroll() {
    if (this._ticking) {
      return;
    }

    this._ticking = true;
    requestAnimationFrame(() => {
      this._updateHeaderVisibility();
      this._ticking = false;
    });
  }

  _updateHeaderVisibility() {
    if (!this._desktopQuery.matches) {
      return;
    }

    const currentY = window.scrollY;
    const delta = currentY - this._lastScrollY;

    if (Math.abs(delta) < SCROLL_DELTA_THRESHOLD) {
      return;
    }

    if (this._searchOpen) {
      // Keep the header pinned and visible while searching
      this._setState(HeaderState.PINNED);
      this._lastScrollY = currentY;
      return;
    }

    const headerHeight = this._header.offsetHeight;

    if (delta > 0) {
      // Scrolling down: let the header flow away naturally near the top,
      // then slide it out of view once it has been scrolled past
      this._setState(currentY <= headerHeight ? HeaderState.FLOW : HeaderState.UNPINNED);
    } else if (currentY <= 0) {
      this._setState(HeaderState.FLOW);
    } else if (this._state === HeaderState.FLOW && currentY <= headerHeight) {
      // Still within the header's own height and never left the flow — scroll
      // back naturally rather than snapping the header to a fixed position
      this._setState(HeaderState.FLOW);
    } else {
      // Scrolling up after the header has left the viewport: reveal it fixed
      this._setState(HeaderState.PINNED);
    }

    this._lastScrollY = currentY;
  }

  _reveal() {
    if (this._desktopQuery.matches && this._state !== HeaderState.FLOW) {
      this._setState(HeaderState.PINNED);
    }
  }

  _setState(next) {
    if (next === this._state) {
      return;
    }

    if (next === HeaderState.UNPINNED) {
      // The header (and any open dropdowns within it) is sliding off-screen
      document.dispatchEvent(new CustomEvent('chh-header:hidden'));
    }

    if (next === HeaderState.FLOW) {
      this._header.classList.remove('header--fixed', 'header--hidden');
    } else if (this._state === HeaderState.FLOW) {
      // Entering the fixed states from normal flow: set the initial position
      // without a transition so it doesn't animate from its in-flow position
      this._header.classList.add('header--no-transition', 'header--fixed');
      this._header.classList.toggle('header--hidden', next === HeaderState.UNPINNED);
      // Force a reflow so the next transform change animates
      void this._header.offsetHeight;
      this._header.classList.remove('header--no-transition');
    } else {
      // Already fixed: toggling hidden animates the slide via CSS transition
      this._header.classList.toggle('header--hidden', next === HeaderState.UNPINNED);
    }

    this._state = next;
  }

  _setOpen(open) {
    this._toggle.setAttribute('aria-expanded', String(open));
    this._nav.hidden = !open;
    this._setBodyScrollLock(open);
    this._focusTrap.active = open;

    if (!open) {
      this._mobileNav?.reset();
    }
  }

  _setBodyScrollLock(locked) {
    const { body } = document;

    if (locked) {
      // Pin the body at its current scroll offset (see body.unscrollable in the
      // stylesheet) so iOS Safari can't scroll the page behind the open nav.
      this._scrollY = window.scrollY;
      body.style.top = `-${this._scrollY}px`;
      body.classList.add('unscrollable');
    } else {
      body.classList.remove('unscrollable');
      body.style.top = '';
      window.scrollTo(0, this._scrollY ?? 0);
    }
  }
}

customElements.define('chh-header', HeaderElement);

export default HeaderElement;
